import { useState } from "react";
import { inp, lbl, btnP, btnS } from "../../../lib/styles.js";
import { C, font, Drawer, Chip, eyebrow } from "../components/ui.jsx";
import { RECIPES, CONTROL_SETS, getRecipe } from "../lib/recipes.js";

const SUBJECT_TYPES = [
  { id: "person", label: "Person" },
  { id: "vehicle", label: "Vehicle" },
  { id: "prop", label: "Prop" },
  { id: "other", label: "Object" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

// New processing job: pick a recipe, point it at a set of photos, tweak the controls,
// add to the queue. Phase 1 = local state only (the parent appends a mock job).
export default function NewJobPanel({ onClose, onSubmit, initialRecipeId, shoots = [] }) {
  const [recipeId, setRecipeId] = useState(initialRecipeId || "");
  const [label, setLabel] = useState("");
  const [shootLabel, setShootLabel] = useState("");
  const [subjectType, setSubjectType] = useState("person");
  const [controlSet, setControlSet] = useState("body");
  const [source, setSource] = useState("");
  const [priority, setPriority] = useState(false);

  const recipe = getRecipe(recipeId);
  const canSubmit = recipeId && label.trim();

  const submit = () => {
    if (!canSubmit) return;
    const trimmedShoot = shootLabel.trim();
    const existing = shoots.find((s) => s.label.toLowerCase() === trimmedShoot.toLowerCase());
    const shoot = trimmedShoot
      ? existing || { id: slug(trimmedShoot), label: trimmedShoot }
      : { id: "unassigned", label: "Unassigned" };
    onSubmit({
      label: label.trim(),
      shoot,
      subjectType,
      recipeId,
      controlSet,
      source: source.trim() || "captures/—",
      priority: priority ? 1 : 0,
    });
  };

  return (
    <Drawer
      title="New Processing Job"
      subtitle="Pick a recipe and point it at a set of photos"
      onClose={onClose}
      footer={
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button style={btnS} onClick={onClose}>Cancel</button>
          <button style={{ ...btnP, opacity: canSubmit ? 1 : 0.45, cursor: canSubmit ? "pointer" : "not-allowed" }} onClick={submit}>
            Add to Queue
          </button>
        </div>
      }
    >
      {/* Recipe picker */}
      <div style={{ ...eyebrow, marginBottom: 8 }}>Recipe</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 22 }}>
        {RECIPES.map((r) => {
          const selected = r.id === recipeId;
          return (
            <div
              key={r.id}
              onClick={() => setRecipeId(r.id)}
              style={{
                border: `1px solid ${selected ? C.amber : C.border}`,
                background: selected ? `${C.amber}0d` : C.surface,
                borderRadius: 8,
                padding: 12,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{r.name}</span>
                <span
                  style={{
                    width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                    border: `1.5px solid ${selected ? C.amber : C.border}`,
                    background: selected ? C.amber : "transparent",
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                {r.variants.map((v) => v.name).join(" + ")} · {r.variants.flatMap((v) => v.formats).filter((f, i, a) => a.indexOf(f) === i).join(" / ")}
              </div>
            </div>
          );
        })}
      </div>

      {/* Job details */}
      <div style={{ ...eyebrow, marginBottom: 8 }}>Job details</div>

      <label style={lbl}>Shoot</label>
      <input
        style={{ ...inp, marginBottom: 14 }}
        list="ac-shoots"
        value={shootLabel}
        placeholder="e.g. Booth Day · Jun 2 (groups scans together)"
        onChange={(e) => setShootLabel(e.target.value)}
      />
      <datalist id="ac-shoots">
        {shoots.map((s) => <option key={s.id} value={s.label} />)}
      </datalist>

      <label style={lbl}>Label</label>
      <input style={{ ...inp, marginBottom: 14 }} value={label} placeholder="e.g. Subject · Standing A-pose" onChange={(e) => setLabel(e.target.value)} />

      <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Subject type</label>
          <select style={inp} value={subjectType} onChange={(e) => setSubjectType(e.target.value)}>
            {SUBJECT_TYPES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Control set</label>
          <select style={inp} value={controlSet} onChange={(e) => setControlSet(e.target.value)}>
            {CONTROL_SETS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <label style={lbl}>Photo source</label>
      <input style={{ ...inp, marginBottom: 14, fontFamily: font }} value={source} placeholder="captures/cap_045 or a folder path" onChange={(e) => setSource(e.target.value)} />

      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text, cursor: "pointer" }}>
        <input type="checkbox" checked={priority} onChange={(e) => setPriority(e.target.checked)} />
        High priority — jump the queue
      </label>

      {/* Output summary */}
      {recipe && (
        <div style={{ marginTop: 22, padding: 14, background: C.surfaceAlt, borderRadius: 8 }}>
          <div style={{ ...eyebrow, marginBottom: 8 }}>Will produce</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {recipe.variants.map((v) => (
              <div key={v.name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text }}>
                <span style={{ fontWeight: 500, minWidth: 42 }}>{v.name}</span>
                <span style={{ color: C.muted }}>{v.texture} texture</span>
                <span style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                  {v.formats.map((f) => <Chip key={f} color={C.muted}>{f}</Chip>)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Drawer>
  );
}
